import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Smartphone, Laptop, Tablet, Download, TrendingUp, Users,
  RefreshCw, Search, Send, X, CheckSquare, Square, Clock, Calendar, Trash2,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

/**
 * Installed Devices — which players added the web app to their home screen.
 *
 * Live counters (auto-refreshes silently every 30s):
 *   • Distinct devices installed
 *   • Unique players installed (same player on Android + iPhone = 2 devices, 1 player)
 *   • Dismissed (funnel signal — how many got the prompt but said no)
 *   • Per-platform breakdown pill
 *
 * List below: most recent install per (player, platform) tuple. Sorting +
 * search by email/username, and a manual refresh button.
 */

function platformIcon(platform) {
  const p = (platform || '').toLowerCase();
  if (p === 'ios' || p === 'android') return <Smartphone className="w-3.5 h-3.5" />;
  if (p === 'desktop') return <Laptop className="w-3.5 h-3.5" />;
  return <Tablet className="w-3.5 h-3.5" />;
}

function platformLabel(p) {
  const m = { ios: 'iOS', android: 'Android', desktop: 'Desktop', other: 'Other' };
  return m[(p || 'other').toLowerCase()] || p;
}

export default function InstalledDevicesPage() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [composerOpen, setComposerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get('/api/admin/pwa/install-stats').catch(() => ({ data: null })),
        api.get('/api/admin/pwa/installs').catch(() => ({ data: { items: [] } })),
      ]);
      setStats(s.data);
      setRows(l.data?.items || []);
    } catch (e) {
      toast.error('Failed to load install data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (r.user_email || '').toLowerCase().includes(needle) ||
      (r.user_name || '').toLowerCase().includes(needle) ||
      (r.platform_hint || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="p-6 space-y-6" data-testid="installed-devices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Download className="w-6 h-6 text-[#D4AF37]" /> Installed Devices
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Players who added Vegas Lobby to their home screen. Live view — no refresh needed.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="ghost" className="gap-2" data-testid="pwa-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Selection action bar — only shows when at least one row is selected */}
      {selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-3"
          data-testid="push-selection-bar"
        >
          <div className="text-sm text-white">
            <span className="font-semibold text-[#D4AF37]">{selected.size}</span> player{selected.size === 1 ? '' : 's'} selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="push-clear-selection">Clear</Button>
            <Button
              size="sm"
              className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold"
              onClick={() => setComposerOpen(true)}
              data-testid="push-open-composer"
            >
              <Send className="w-4 h-4 mr-2" /> Send Push
            </Button>
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI
          icon={<Smartphone className="w-4 h-4 text-[#D4AF37]" />}
          label="Installed Devices"
          value={stats?.distinct_devices ?? '—'}
          testid="kpi-devices"
        />
        <KPI
          icon={<Users className="w-4 h-4 text-emerald-400" />}
          label="Unique Players"
          value={stats?.unique_users ?? '—'}
          testid="kpi-users"
        />
        <KPI
          icon={<TrendingUp className="w-4 h-4 text-fuchsia-300" />}
          label="Total Installs"
          value={stats?.total_install_events ?? '—'}
          testid="kpi-events"
        />
        <KPI
          icon={<Download className="w-4 h-4 text-gray-400" />}
          label="Dismissed"
          value={stats?.dismissed_count ?? '—'}
          testid="kpi-dismissed"
        />
      </div>

      {/* Platform pills */}
      {stats?.by_platform && Object.keys(stats.by_platform).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="platform-pills">
          {Object.entries(stats.by_platform).map(([p, count]) => (
            <span key={p} className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#131822] border border-white/5 text-gray-300 flex items-center gap-1.5">
              {platformIcon(p)} {platformLabel(p)} · <span className="text-[#D4AF37] font-semibold">{count}</span>
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, name, or platform…"
          className="pl-9 bg-[#131822] border-white/10"
          data-testid="installs-search"
        />
      </div>

      {/* Devices table */}
      <div className="bg-[#131822] border border-white/5 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 text-[10px] uppercase text-gray-500 px-4 py-3 border-b border-white/5">
          <div className="col-span-1 flex items-center">
            <button
              onClick={() => {
                if (selected.size === filtered.length) setSelected(new Set());
                else setSelected(new Set(filtered.map((r) => r.user_id)));
              }}
              className="text-gray-400 hover:text-[#D4AF37]"
              data-testid="push-select-all"
              title="Select all"
            >
              {selected.size > 0 && selected.size === filtered.length ? (
                <CheckSquare className="w-3.5 h-3.5 text-[#D4AF37]" />
              ) : (
                <Square className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
          <div className="col-span-3">Player</div>
          <div className="col-span-2">Platform</div>
          <div className="col-span-2">Outcome</div>
          <div className="col-span-2">Installed</div>
          <div className="col-span-2 truncate">User Agent</div>
        </div>
        {filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500 text-sm">
            {loading ? 'Loading…' : 'No installs recorded yet. Share the app with your players to grow this list.'}
          </div>
        ) : (
          filtered.map((r) => {
            const sel = selected.has(r.user_id);
            const toggle = () => {
              const next = new Set(selected);
              if (sel) next.delete(r.user_id);
              else next.add(r.user_id);
              setSelected(next);
            };
            return (
              <div key={`${r.user_id}-${r.platform_hint}`} className={`grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-white/5 text-sm ${sel ? 'bg-[#D4AF37]/5' : 'hover:bg-white/5'}`} data-testid={`install-row-${r.user_id}`}>
                <div className="col-span-1">
                  <button onClick={toggle} className="text-gray-400 hover:text-[#D4AF37]" data-testid={`push-select-${r.user_id}`}>
                    {sel ? <CheckSquare className="w-4 h-4 text-[#D4AF37]" /> : <Square className="w-4 h-4" />}
                  </button>
                </div>
                <div className="col-span-3 min-w-0">
                  <div className="text-white font-medium truncate">{r.user_name || '—'}</div>
                  <div className="text-[11px] text-gray-500 truncate">{r.user_email || '—'}</div>
                </div>
                <div className="col-span-2 flex items-center gap-1.5 text-gray-300 text-xs">
                  {platformIcon(r.platform_hint)} {platformLabel(r.platform_hint)}
                </div>
                <div className="col-span-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    r.outcome === 'appinstalled' ? 'bg-emerald-500/15 text-emerald-400'
                    : r.outcome === 'accepted' ? 'bg-[#D4AF37]/15 text-[#D4AF37]'
                    : 'bg-white/5 text-gray-400'
                  }`}>
                    {r.outcome === 'appinstalled' ? 'Installed' : r.outcome === 'accepted' ? 'Accepted' : r.outcome}
                  </span>
                </div>
                <div className="col-span-2 text-[11px] text-gray-500">
                  {r.installed_at ? new Date(r.installed_at).toLocaleString() : '—'}
                </div>
                <div className="col-span-2 text-[10px] text-gray-600 truncate" title={r.user_agent}>
                  {r.user_agent || '—'}
                </div>
              </div>
            );
          })
        )}
      </div>

      {composerOpen && (
        <PushComposerModal
          userIds={Array.from(selected)}
          rows={rows.filter((r) => selected.has(r.user_id))}
          onClose={() => setComposerOpen(false)}
          onSent={() => {
            setComposerOpen(false);
            setSelected(new Set());
          }}
        />
      )}

      {/* Scheduled pushes: any admin-queued deliveries waiting to fire. */}
      <ScheduledPushesPanel />
    </div>
  );
}

function KPI({ icon, label, value, testid }) {
  return (
    <div className="bg-[#131822] border border-white/5 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] text-gray-500 uppercase flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}

/**
 * Compose + send a push notification to the currently-selected installed
 * players. Can send immediately (POST /api/admin/push/send-targeted) or
 * schedule for a future time (POST /api/admin/push/schedule).
 */
function PushComposerModal({ userIds, rows, onClose, onSent }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/app');
  const [sending, setSending] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  // Default 30 minutes from now, formatted for <input type="datetime-local">.
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date(Date.now() + 30 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    if (scheduleOn) {
      const when = new Date(scheduledAt);
      if (Number.isNaN(when.getTime())) {
        toast.error('Pick a valid date/time');
        return;
      }
      if (when.getTime() <= Date.now()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
    }
    setSending(true);
    try {
      if (scheduleOn) {
        const res = await api.post('/api/admin/push/schedule', {
          title: title.trim(),
          body: body.trim(),
          url: url.trim() || '/app',
          user_ids: userIds,
          scheduled_for: new Date(scheduledAt).toISOString(),
        });
        const d = res.data || {};
        toast.success(`Scheduled for ${new Date(d.scheduled_for).toLocaleString()} (${d.audience === 'all' ? 'all subscribers' : `${d.target_count} players`})`);
      } else {
        const res = await api.post('/api/admin/push/send-targeted', {
          title: title.trim(),
          body: body.trim(),
          url: url.trim() || '/app',
          user_ids: userIds,
        });
        const data = res.data || {};
        if (data.players_reached === 0) {
          toast.warning(`Sent 0 of ${data.total_players_targeted || userIds.length} — selected players have no active push subscriptions.`);
        } else {
          toast.success(`Reached ${data.players_reached} of ${data.total_players_targeted} players (${data.sent} devices)`);
        }
      }
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send push');
    } finally {
      setSending(false);
    }
  };

  const preview = rows.slice(0, 4);
  const more = Math.max(0, rows.length - preview.length);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-[#131822] border border-white/10 rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="push-composer-modal"
      >
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#D4AF37]" />
            <div>
              <div className="font-bold text-white">Send Push Notification</div>
              <div className="text-[11px] text-gray-500">{userIds.length} player{userIds.length === 1 ? '' : 's'} selected</div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Recipient preview */}
          <div className="flex flex-wrap gap-1.5" data-testid="push-recipients">
            {preview.map((r) => (
              <span key={r.user_id} className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-300">
                {r.user_email || r.user_name || r.user_id.slice(0, 8)}
              </span>
            ))}
            {more > 0 && (
              <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-500">+{more} more</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="We miss you!"
              maxLength={50}
              className="bg-[#0B0E14] border-white/10"
              data-testid="push-title"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Message *</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="25 FP bonus waiting — claim before Friday."
              maxLength={180}
              rows={3}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#D4AF37]/60"
              data-testid="push-body"
            />
            <div className="text-[10px] text-gray-600 mt-1 text-right">{body.length}/180</div>
          </div>
          <div>
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Tap URL</label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/app"
              className="bg-[#0B0E14] border-white/10"
              data-testid="push-url"
            />
            <div className="text-[10px] text-gray-500 mt-1">Relative URL inside the app. Use <code>/app?tab=rewards</code> to deep-link.</div>
          </div>

          {/* Schedule-for-later toggle + datetime picker. */}
          <div className="rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={scheduleOn}
                onChange={(e) => setScheduleOn(e.target.checked)}
                className="accent-[#D4AF37]"
                data-testid="push-schedule-toggle"
              />
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm text-white font-medium">Schedule for later</span>
            </label>
            {scheduleOn && (
              <div className="mt-2.5">
                <label className="block text-[11px] uppercase text-gray-500 mb-1">Fire at (your local time)</label>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="bg-[#131822] border-white/10"
                  data-testid="push-schedule-datetime"
                />
                <div className="text-[10px] text-gray-500 mt-1">
                  The worker checks every 30s and dispatches on or after this time.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || !title.trim() || !body.trim()}
            className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold"
            data-testid="push-send"
          >
            {sending
              ? (scheduleOn ? 'Scheduling…' : 'Sending…')
              : scheduleOn
                ? (<><Calendar className="w-4 h-4 mr-2" /> Schedule for {userIds.length}</>)
                : (<><Send className="w-4 h-4 mr-2" /> Send to {userIds.length}</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}


/**
 * Lists upcoming and recently-fired scheduled pushes. Admins can cancel
 * anything still pending. Refreshes silently every 15s so the list stays
 * accurate as the background worker dispatches entries.
 */
function ScheduledPushesPanel() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/admin/push/scheduled');
      setPending(res.data?.pending || []);
      setHistory(res.data?.history || []);
    } catch {
      if (!silent) toast.error('Failed to load scheduled pushes');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, []);

  const cancel = async (id) => {
    try {
      await api.delete(`/api/admin/push/scheduled/${id}`);
      toast.success('Scheduled push cancelled');
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (!pending.length && !history.length && !loading) return null;

  return (
    <div className="bg-[#131822] border border-white/5 rounded-2xl overflow-hidden" data-testid="scheduled-pushes-panel">
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#D4AF37]" />
          <h2 className="text-sm font-semibold text-white">Scheduled Pushes</h2>
          <span className="text-[11px] text-gray-500">{pending.length} pending</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => load()} disabled={loading} className="gap-1" data-testid="scheduled-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {pending.length > 0 && (
        <div className="divide-y divide-white/5">
          {pending.map((p) => (
            <ScheduledRow key={p.id} item={p} onCancel={() => cancel(p.id)} />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <>
          <div className="px-4 py-2 text-[10px] uppercase text-gray-500 bg-white/[0.02]">History</div>
          <div className="divide-y divide-white/5">
            {history.map((p) => (
              <ScheduledRow key={p.id} item={p} />
            ))}
          </div>
        </>
      )}

      {pending.length === 0 && history.length === 0 && !loading && (
        <div className="px-4 py-8 text-center text-sm text-gray-500">
          No scheduled pushes yet. Use the composer above with <span className="text-[#D4AF37]">Schedule for later</span> to queue one.
        </div>
      )}
    </div>
  );
}

function ScheduledRow({ item, onCancel }) {
  const audience = item.audience === 'all'
    ? 'All subscribers'
    : `${(item.user_ids || []).length} player${(item.user_ids || []).length === 1 ? '' : 's'}`;
  const fireAt = item.scheduled_for ? new Date(item.scheduled_for).toLocaleString() : '—';
  const statusColors = {
    pending: 'bg-[#D4AF37]/15 text-[#D4AF37]',
    sending: 'bg-blue-500/15 text-blue-400',
    sent: 'bg-emerald-500/15 text-emerald-400',
    cancelled: 'bg-gray-500/15 text-gray-400',
    failed: 'bg-red-500/15 text-red-400',
  };
  const resultStr = item.result
    ? item.result.mode === 'targeted'
      ? `${item.result.players_reached}/${item.result.total_targets} reached · ${item.result.sent} devices`
      : `${item.result.sent}/${item.result.total} devices`
    : null;

  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm" data-testid={`scheduled-row-${item.id}`}>
      <div className="col-span-4 min-w-0">
        <div className="text-white font-medium truncate">{item.title}</div>
        <div className="text-[11px] text-gray-500 truncate">{item.body}</div>
      </div>
      <div className="col-span-2 text-xs text-gray-300">{audience}</div>
      <div className="col-span-3 text-xs text-gray-400 flex items-center gap-1">
        <Calendar className="w-3.5 h-3.5" /> {fireAt}
      </div>
      <div className="col-span-2">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[item.status] || 'bg-white/5 text-gray-400'}`}>
          {item.status}
        </span>
        {resultStr && <div className="text-[10px] text-gray-500 mt-0.5 truncate">{resultStr}</div>}
      </div>
      <div className="col-span-1 text-right">
        {item.status === 'pending' && onCancel && (
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400"
            title="Cancel"
            data-testid={`scheduled-cancel-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
